/**
 * 开局页的表单模型。纯函数 + 纯数据，没有 React、没有 store —— 所以能直接单测。
 *
 * 校验规则是引擎 `validateConfig` 的**前置镜像**：引擎拒绝的组合在这里就要报错，
 * 否则用户点「开始游戏」之后只会收到一个 `INVALID_CONFIG` 的 toast，不知道该改哪一项。
 */
import type { Chips } from '@poker/shared'
import { SEAT_LIMITS } from '@poker/shared'

import {
  clampSeatCount,
  defaultNameFor,
  makeLocalPlayers,
  normalizeName,
  seatCountOptions,
} from '../local/localSeats.js'
import type { LocalSetup } from '../transport/LocalEngineTransport.js'

export interface LocalSetupForm {
  readonly seatCount: number
  /** 长度恒等于 SEAT_LIMITS.MAX，多出来的部分留着，改人数时不会把已填的名字丢掉 */
  readonly names: readonly string[]
  readonly smallBlind: Chips
  readonly bigBlind: Chips
  readonly startingStack: Chips
}

export interface BlindPreset {
  readonly label: string
  readonly smallBlind: Chips
  readonly bigBlind: Chips
}

export const BLIND_PRESETS: readonly BlindPreset[] = [
  { label: '5 / 10', smallBlind: 5, bigBlind: 10 },
  { label: '10 / 20', smallBlind: 10, bigBlind: 20 },
  { label: '25 / 50', smallBlind: 25, bigBlind: 50 },
  { label: '50 / 100', smallBlind: 50, bigBlind: 100 },
]

export const STACK_PRESETS: readonly Chips[] = [1000, 2000, 5000, 10_000]

/** 起始筹码至少要够 20 个大盲：低于这个数第一手就有人被盲注推成全下，牌局根本展不开。 */
export const MIN_STACK_IN_BB = 20

export const DEFAULT_LOCAL_SETUP_FORM: LocalSetupForm = {
  seatCount: 4,
  names: Array.from({ length: SEAT_LIMITS.MAX }, (_, i) => defaultNameFor(i)),
  smallBlind: 10,
  bigBlind: 20,
  startingStack: 2000,
}

export function setSeatCount(f: LocalSetupForm, n: number): LocalSetupForm {
  return { ...f, seatCount: clampSeatCount(n) }
}

export function setName(f: LocalSetupForm, seat: number, raw: string): LocalSetupForm {
  if (seat < 0 || seat >= f.names.length) return f
  const names = f.names.slice()
  // 这里**不** normalize：normalize 会把中间的空格吃掉，用户还在打字就被改光标位置。
  // 归一化推迟到 toLocalSetup / 校验时做。
  names[seat] = raw
  return { ...f, names }
}

export function setBlinds(f: LocalSetupForm, smallBlind: Chips, bigBlind: Chips): LocalSetupForm {
  return { ...f, smallBlind, bigBlind }
}

export function setStartingStack(f: LocalSetupForm, startingStack: Chips): LocalSetupForm {
  return { ...f, startingStack }
}

/** 参与本局的昵称（已归一化，长度 === seatCount）。 */
export function effectiveNames(f: LocalSetupForm): string[] {
  const out: string[] = []
  for (let seat = 0; seat < f.seatCount; seat++) {
    out.push(normalizeName(f.names[seat] ?? '', defaultNameFor(seat)))
  }
  return out
}

/** 返回错误文案数组；空数组 = 可以开局。 */
export function validateLocalSetupForm(f: LocalSetupForm): string[] {
  const errors: string[] = []
  if (!Number.isInteger(f.seatCount) || f.seatCount < SEAT_LIMITS.MIN || f.seatCount > SEAT_LIMITS.MAX) {
    errors.push(`人数必须在 ${SEAT_LIMITS.MIN}~${SEAT_LIMITS.MAX} 之间`)
  }
  if (!Number.isInteger(f.bigBlind) || f.bigBlind < 2) {
    errors.push('大盲必须是不小于 2 的整数')
  }
  if (!Number.isInteger(f.smallBlind) || f.smallBlind < 1) {
    errors.push('小盲必须是不小于 1 的整数')
  }
  if (Number.isInteger(f.smallBlind) && Number.isInteger(f.bigBlind) && f.smallBlind > f.bigBlind) {
    errors.push('小盲不能大于大盲')
  }
  if (!Number.isInteger(f.startingStack) || f.startingStack < 1) {
    errors.push('起始筹码必须是正整数')
  } else if (Number.isInteger(f.bigBlind) && f.startingStack < f.bigBlind * MIN_STACK_IN_BB) {
    errors.push(`起始筹码至少要有 ${MIN_STACK_IN_BB} 个大盲（$${f.bigBlind * MIN_STACK_IN_BB}）`)
  }
  // 重名会让遮牌页的「把手机交给 XXX」失去意义 —— 那正是防递错手机的唯一提示
  const names = effectiveNames(f)
  const seen = new Set<string>()
  for (const n of names) {
    if (seen.has(n)) {
      errors.push(`昵称「${n}」重复了，遮牌页会分不清该给谁`)
      break
    }
    seen.add(n)
  }
  return errors
}

export function canStart(f: LocalSetupForm): boolean {
  return validateLocalSetupForm(f).length === 0
}

/** 表单 → Transport 的入参。座位号 0..seatCount-1 连续占满，`maxSeats` 就是人数。 */
export function toLocalSetup(f: LocalSetupForm): LocalSetup {
  return {
    players: makeLocalPlayers(f.seatCount, f.startingStack, effectiveNames(f)),
    smallBlind: f.smallBlind,
    bigBlind: f.bigBlind,
    ante: 0,
    anteMode: 'none',
    startingStack: f.startingStack,
    roomName: '本地牌局',
    // 自动开下一手：本地模式没有「准备」按钮，靠一个固定的间隔让大家看完结果再继续。
    // 想跳过等待就调 `nextLocalHand()`。
    autoNextHand: true,
  }
}

export { seatCountOptions }
